import React, { useState } from "react";
import { fileUrl, api } from "@/lib/api";
import { Trash2, Upload, GripVertical, Star } from "lucide-react";

/**
 * Drag-to-reorder image gallery for the admin edit page.
 * Uses native HTML5 drag-and-drop. The first image is highlighted as the "Hero" image.
 */
export default function SortableImageGrid({
  productId,
  images,
  onImagesChange,
  onAddFiles,
}) {
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  const remove = async (fileId) => {
    if (!window.confirm("Remove this image from the product?")) return;
    try {
      await api.delete(`/products/${productId}/images/${fileId}`);
      onImagesChange(images.filter((i) => i.id !== fileId));
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed to remove image");
    }
  };

  const persistOrder = async (next) => {
    onImagesChange(next);
    try {
      await api.patch(`/products/${productId}/images/order`, {
        ordered_ids: next.map((i) => i.id),
      });
    } catch (e) {
      console.error(e);
    }
  };

  const onDragStart = (idx) => (e) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (idx) => (e) => {
    e.preventDefault();
    setOverIdx(idx);
  };
  const onDrop = (idx) => (e) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) {
      setDragIdx(null);
      setOverIdx(null);
      return;
    }
    const next = [...images];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setDragIdx(null);
    setOverIdx(null);
    persistOrder(next);
  };
  const onDragEnd = () => {
    setDragIdx(null);
    setOverIdx(null);
  };
  const makeHero = (idx) => {
    if (idx === 0) return;
    const next = [...images];
    const [moved] = next.splice(idx, 1);
    next.unshift(moved);
    persistOrder(next);
  };

  return (
    <div>
      <div className="grid grid-cols-3 gap-2">
        {images.map((img, idx) => (
          <div
            key={img.id}
            draggable
            onDragStart={onDragStart(idx)}
            onDragOver={onDragOver(idx)}
            onDrop={onDrop(idx)}
            onDragEnd={onDragEnd}
            data-testid={`sortable-image-${idx}`}
            className={`relative group cursor-move transition-all ${
              dragIdx === idx ? "opacity-40" : ""
            } ${overIdx === idx && dragIdx !== idx ? "ring-2 ring-[#B5953B]" : ""}`}
          >
            <img
              src={fileUrl(img.storage_path)}
              alt=""
              className="w-full h-24 object-cover border border-[#E8E6DF] rounded-sm pointer-events-none"
            />

            {/* Hero badge */}
            {idx === 0 && (
              <div className="absolute top-1 left-1 bg-[#B5953B] text-white text-[9px] font-bold tracking-wider uppercase px-1.5 py-0.5 rounded-sm flex items-center gap-1">
                <Star size={9} strokeWidth={2.5} fill="white" /> Hero
              </div>
            )}

            {/* Drag grip indicator */}
            <div className="absolute top-1 right-1 bg-white/95 border border-[#E8E6DF] rounded-sm p-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <GripVertical size={11} strokeWidth={2} className="text-[#5C5C54]" />
            </div>

            {/* Action buttons (bottom row) */}
            <div className="absolute bottom-1 inset-x-1 flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity gap-1">
              {idx !== 0 && (
                <button
                  type="button"
                  onClick={() => makeHero(idx)}
                  title="Set as hero image"
                  data-testid={`set-hero-${idx}`}
                  className="bg-white/95 border border-[#E8E6DF] rounded-sm p-1 hover:bg-[#B5953B] hover:text-white text-[#1A1A18]"
                >
                  <Star size={11} strokeWidth={2} />
                </button>
              )}
              <button
                type="button"
                onClick={() => remove(img.id)}
                title="Remove image"
                data-testid={`product-image-remove-${idx}`}
                className="bg-white/95 border border-[#E8E6DF] rounded-sm p-1 hover:bg-[#9E2A2B] hover:text-white text-[#1A1A18] ml-auto"
              >
                <Trash2 size={11} strokeWidth={2} />
              </button>
            </div>
          </div>
        ))}
        <label className="border border-dashed border-[#E8E6DF] hover:bg-[#F2F0EA] flex items-center justify-center h-24 cursor-pointer rounded-sm">
          <input
            type="file"
            className="hidden"
            accept="image/*"
            multiple
            onChange={(e) => onAddFiles(e.target.files)}
            data-testid="add-image-input"
          />
          <Upload size={16} strokeWidth={1.5} className="text-[#5C5C54]" />
        </label>
      </div>
      <p className="text-[11px] text-[#5C5C54] mt-2.5 leading-relaxed">
        Drag images to reorder · the first image (gold star) is the hero shown after a QR scan.
      </p>
    </div>
  );
}
